import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserService } from '@pg-app/partner/user-profile/services/user-service';
import { ToastrService } from 'ngx-toastr';
import { UtilityService } from '@nttp/common-service/utility-service';
import { Subject, forkJoin } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { NewRecipientModel } from '@pg-app/client/manage-recipient/models/recipient.model';
import { CommonDialogComponent } from '@pg-app/common/common-dialog/common-dialog.component';
import { ShareService } from '@nttp/common-service/share-service';
import { RolesService } from '@pg-app/core/services/roles/role.servirce';
import { RoleNames } from '@pg-app/core/services/roles/RoleNames';
import { environment } from '@pg-env/environment';

export interface PostCode {
  id: string;
  next: string;
  text: string;
  description: string;
}
export interface AddressDetails {
  city: string;
  label: string;
  countryIso2: string;
  postalCode: string;
}

@Component({
  selector: 'np-user-profile',
  templateUrl: 'user-profile.component.html',
  styleUrls: ['user-profile.component.scss']
})
export class UserProfileComponent implements OnInit {
  userForm: FormGroup;

  collapseContactDetails = false;
  collapseAffiliateDetails = true;
  collapseBankdetails = true;
  collapseSubRef = true;

  titles: Array<any> = [];
  accessLevels: Array<any> = [];
  countrys: Array<any> = [];
  callingCodes: Array<any> = [];
  currencies = [];

  destroy$ = new Subject<boolean>();

  bankId = '';
  currencyId = '';

  acceptMarketingEmails = false;
  submitForm = false;
  acceptPolicy = true;

  callingCodeSelect = null;
  clientData = null;
  isPartnerAdmin = null;
  isPrivalgoMarkets = environment.isPrivalgoMarkets;
  domain = environment.domain;
  brandName = environment.brandName;

  constructor(
    private fb: FormBuilder,
    private utilityService: UtilityService,
    private userService: UserService,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private router: Router,
    public newRecipientModel: NewRecipientModel,
    private shareService: ShareService,
    private roleService: RolesService,
  ) { }

  ngOnInit() {
    this.onInit();
    this.newRecipientModel.clear();
    this.hasRoleOfPartnerAdmin();
  }

  onInit() {
    this.userForm = this.fb.group(
      {
        title: '',
        firstName: '',
        middleName: '',
        lastName: '',
        email: '',
        callingCode: '',
        phoneNumber: '',
        businessName: '',
        accessLevel: '',
        accountNumber: '',
        address1: '',
        address2: '',
        address3: '',
        city: '',
        postCode: '',
        country: '',

        // bank
        bankName: '',
        bankCode: '',
        IBAN: '',
        bankAccountNumber: '',
        bankSortCode: '',
        SWIFTCode: '',
        bankAddress1: '',
        bankAddress2: '',
        bankAddress3: '',
        bankCity: '',
        stateProvince: '',
        zipPostalCode: '',
        currency: '',
        bankCountry: '',
        reference: '',
      },
    );

    const getTitle = this.shareService.titleList$;
    const getCallingCodes = this.shareService.callingCodeList$;
    const getCountries = this.shareService.countryList$;
    const getAccessLevels = this.utilityService.getAccessLevels();
    const getCurrency = this.utilityService.getCurrencies();
    const getUserDetail = this.userService.getUserDetail(true);

    forkJoin([getUserDetail, getTitle, getCallingCodes, getCountries, getAccessLevels, getCurrency]).subscribe(res => {
      this.getAllData(res);
    });
  }

  getAllData(data) {
    this.titles = data[1] || [];
    this.accessLevels = data[4] || [];
    this.countrys = data[3] || [];
    this.callingCodes = data[2] || [];
    this.currencies = data[5];
    this.getUserProfile(data[0]);
  }

  onSubmit() {
    this.userForm.markAllAsTouched();
    this.collapseContactDetails = false;
    this.collapseAffiliateDetails = false;
    this.collapseBankdetails = false;
    this.collapseSubRef = false;
    this.submitForm = true;
    if (this.userForm.valid && this.acceptPolicy) {
      this.updateProfile();
    }
  }

  private updateProfile() {
    const accountDetail = {
      organizationInfo: {
        businessName: this.userForm.get('businessName').value ? this.userForm.get('businessName').value : null,
      },
      bankId: this.bankId ? this.bankId : null,
      currencyId: this.currencyId ? this.currencyId : null,
      leadType: this.clientData.leadType,
      firstName: this.clientData.firstName,
      middleName: this.clientData.middleName ? this.clientData.middleName : null,
      lastName: this.clientData.lastName,
      email: this.clientData.email,
      phoneNumber: this.clientData.phoneNumber,
      doNotBulkEmail: !this.acceptMarketingEmails,
    };

    this.userService.updateUserDetail(accountDetail).subscribe(
      data => {
        this.dialog.open(CommonDialogComponent, {
          disableClose: true,
          data: {
            title: 'Profile updated successfully!',
            dialogType: 1,
            routingUrl: 'partner/dashboard'
          }
        });
      }
    );
  }

  private getUserProfile(data) {
    const phoneNumber = data.phoneNumber.split(' ').join('');
    this.callingCodeSelect = this.callingCodes.find(item => item.id === data.callingCode.id);
    this.userForm.patchValue({
      email: data.email,
      firstName: data.firstName,
      middleName: data.middleName,
      lastName: data.lastName,
      title: data.title.titleId,
      callingCode: data.callingCode.id,
      phoneNumber: phoneNumber,
      businessName: data.organizationInfo?.businessName,
      accessLevel: data.accessLevelId,
      accountNumber: data.accountNumber,
      address1: data.address1,
      address2: data.address2,
      address3: data.address3,
      city: data.city,
      postCode: data.postCode,
      country: data.country.id,
    });

    if (data.bank) {
      this.bankId = data.bankId;
      this.currencyId = data.currencyId;
      const bank = data.bank;
      this.userForm.patchValue({
        bankName: bank.name,
        bankCode: bank.bankCode,
        IBAN: bank.iBan,
        bankAccountNumber: bank.accountNumber,
        bankSortCode: bank.sortCodeUK,
        SWIFTCode: bank.sWiftCode,
        bankAddress1: bank.address1,
        bankAddress2: bank.address2,
        bankAddress3: bank.address3,
        bankCity: bank.city,
        stateProvince: bank.province,
        zipPostalCode: bank.postCode,
        currency: data.currencyId,
        bankCountry: bank.countryId,
        reference: bank.reference,
      });
    }

    this.acceptMarketingEmails = !data.doNotBulkEmail;
    this.clientData = {
      leadType: data.leadType,
      firstName: data.firstName,
      middleName: data.middleName,
      lastName: data.lastName,
      email: data.email,
      phoneNumber: data.phoneNumber,
    };
  }

  cancel() {
    this.dialog.open(CommonDialogComponent, {
      disableClose: true,
      data: {
        title: 'Are you sure to remove all changes?',
        dialogType: 3,
        routingUrl: 'partner/dashboard'
      }
    });
  }

  hasRoleOfPartnerAdmin() {
    this.roleService.getRoles(RoleNames.MANAGE_USERS).then(res => {
       this.isPartnerAdmin = !!res;
    });
  }

  addBankDetail() {
    if (!this.isPartnerAdmin){
      this.toastr.error(`You don't have permission to add bank details. Please contact your Partner Admin.`);
      return;
    }
    this.router.navigate([`partner/user-profile/bank-account-detail`]);
  }
}
